import { Pass, Resolution } from 'postprocessing';
import {
    Texture,
    TextureDataType,
    Uniform,
    Vector2,
    WebGLRenderer,
    WebGLRenderTarget,
    ShaderMaterial,
    WebGLArrayRenderTarget,
    Scene,
    PerspectiveCamera,
    DepthTexture,
    TextureLoader,
} from 'three';
import { kawasePassRenderTargetOptions } from './../lightStreakPPEffectPasses';
import { KawasePassProps } from './KawaseLightStreakPostProcessEffects';
import postprocessing_VERT from '../shaders/postprocessing_VERT.glsl';
import KawaseLightStreakBlurPass_FRAG from '../shaders/KawaseLightStreakBlurPass_FRAG.glsl';
import { AddMaterial } from './../AddMaterial';

/** Local TextureLoader — hd_conf imports this from gltfMaterials; neo doesn't need that module. */
const textureLoader = new TextureLoader();
const blurNormalTexture = await textureLoader.loadAsync('/3d/textures/PP_normal.png');

export class KawaseLightStreakBlurPass extends Pass {
    scene3D: Scene;
    camera3D: PerspectiveCamera;
    depthBufferRTarget: WebGLRenderTarget;
    pingPongRTargetA: WebGLRenderTarget;
    pingPongRTargetB: WebGLRenderTarget;
    intermStreaksRTarget: WebGLArrayRenderTarget;
    lightStreakBlurMaterial: ShaderMaterial;
    addMaterial: AddMaterial;
    directions: Vector2[];
    blurPasses: number;
    resolution: Resolution;

    constructor(
        scene3D: Scene,
        camera3D: PerspectiveCamera,
        attenuation: KawasePassProps['attenuation'],
        blurPasses: KawasePassProps['blurPasses'],
        pixelSize: KawasePassProps['pixelSize'],
        directions: KawasePassProps['directions'],
        width: number,
        height: number,
        resolutionScale: number,
        enabled: boolean,
    ) {
        super('KawaseLightStreakBlurPass');

        this.enabled = enabled;
        this.scene3D = scene3D;
        this.camera3D = camera3D;
        this.depthBufferRTarget = new WebGLRenderTarget(1, 1, {
            ...kawasePassRenderTargetOptions,
            depthBuffer: true,
            depthTexture: new DepthTexture(1, 1),
        });
        this.depthBufferRTarget.depthTexture!.name = 'LightStreakBlur.Depth';
        this.pingPongRTargetA = new WebGLRenderTarget(1, 1, kawasePassRenderTargetOptions);
        this.pingPongRTargetA.texture.name = 'LightStreakBlur.Target.A';
        this.pingPongRTargetB = this.pingPongRTargetA.clone();
        this.pingPongRTargetB.texture.name = 'LightStreakBlur.Target.B';
        this.intermStreaksRTarget = new WebGLArrayRenderTarget(1, 1, 2, kawasePassRenderTargetOptions);
        this.intermStreaksRTarget.texture.name = 'LightStreakBlur.Target.Intermediate';

        this.lightStreakBlurMaterial = new ShaderMaterial({
            vertexShader: postprocessing_VERT,
            fragmentShader: KawaseLightStreakBlurPass_FRAG,
            uniforms: {
                cameraNear: new Uniform(this.camera3D.near),
                cameraFar: new Uniform(this.camera3D.far),
                inputBuffer: new Uniform(null),
                depthBuffer: new Uniform(this.depthBufferRTarget.depthTexture),
                normalTex: new Uniform(blurNormalTexture),
                attenuation: new Uniform(attenuation),
                pixelSize: new Uniform(pixelSize),
                pass: new Uniform(0),
                direction: new Uniform(directions[0]),
            },
            toneMapped: false,
            depthWrite: false,
            depthTest: false,
            name: 'KawaseLightStreakBlurPass.Material',
        });

        this.addMaterial = new AddMaterial(this.intermStreaksRTarget.texture);

        this.directions = directions;
        this.blurPasses = blurPasses;

        this.resolution = new Resolution(this, width, height, resolutionScale);

        // this.needsSwap = true;
        // this.renderToScreen = false;
    }

    setSize(_width: number, _height: number): void {
        const resolution = this.resolution;

        this.depthBufferRTarget.setSize(resolution.width / 2, resolution.height / 2);
        this.pingPongRTargetA.setSize(resolution.width, resolution.height);
        this.pingPongRTargetB.setSize(resolution.width, resolution.height);
        this.intermStreaksRTarget.setSize(resolution.width / 2, resolution.height / 2, 2);
    }

    initialize(renderer: WebGLRenderer, _alpha: boolean, frameBufferType: TextureDataType) {
        renderer.initTexture(blurNormalTexture);
        if (frameBufferType !== undefined) {
            this.pingPongRTargetA.texture.type = frameBufferType;
            this.pingPongRTargetA.texture.type = frameBufferType;
            this.intermStreaksRTarget.texture.type = frameBufferType;
        }
    }

    render(
        renderer: WebGLRenderer,
        inputBuffer: WebGLRenderTarget<Texture>,
        outputBuffer: WebGLRenderTarget<Texture> | null,
        _deltaTime?: number | undefined,
        _stencilTest?: boolean | undefined,
    ): void {
        this.fullscreenMaterial = this.lightStreakBlurMaterial;

        renderer.setRenderTarget(this.depthBufferRTarget);
        renderer.render(this.scene3D, this.camera3D);

        (this.fullscreenMaterial as ShaderMaterial).uniforms.depthBuffer.value = this.depthBufferRTarget.depthTexture;
        (this.fullscreenMaterial as ShaderMaterial).uniforms.cameraNear.value = this.camera3D.near;
        (this.fullscreenMaterial as ShaderMaterial).uniforms.cameraFar.value = this.camera3D.far;

        /* Run Pass 2 Times for cross shape */
        for (let i = 0; i < 2; i++) {
            let previousBuffer = inputBuffer;

            (this.fullscreenMaterial as ShaderMaterial).uniforms.direction.value = this.directions[i];

            this.pingPongRTargetA.setSize(this.resolution.width, this.resolution.height);
            this.pingPongRTargetB.setSize(this.resolution.width, this.resolution.height);
            for (let j = 0; j < this.blurPasses; j++) {
                /* Can't read from / write to same buffer in one pass, so 'ping-pong': */
                const buffer = j % 2 === 0 ? this.pingPongRTargetA : this.pingPongRTargetB;

                (this.fullscreenMaterial as ShaderMaterial).uniforms.inputBuffer.value = previousBuffer.texture;
                (this.fullscreenMaterial as ShaderMaterial).uniforms.pass.value = j;

                j > 0 && buffer.setSize(buffer.width / 2, buffer.height / 2);

                renderer.setRenderTarget(buffer);
                renderer.render(this.scene, this.camera);

                previousBuffer = buffer;
            }

            renderer.setRenderTarget(this.intermStreaksRTarget, i);
            renderer.render(this.scene, this.camera);
        }

        this.fullscreenMaterial = this.addMaterial;
        this.addMaterial.uniforms.texArray.value = this.intermStreaksRTarget.texture;

        renderer.setRenderTarget(outputBuffer);
        renderer.render(this.scene, this.camera);
    }
}
